<template>
    <kiosk-layout>
        <div class="flex flex-col gap-12 lg:flex-row">
            <div class="shrink-0 lg:w-60">
                <kiosk-org-units-sidebar v-model="fetchParams"></kiosk-org-units-sidebar>
            </div>
            <div class="w-full">
                <kiosk-org-units-controls v-model="fetchParams" @reset="resetFilters"></kiosk-org-units-controls>
                <page-loading v-if="loading && !list.length"></page-loading>

                <empty-state
                    v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)"
                    @reset="resetFilters"
                />

                <div v-else-if="list.length">
                    <div class="text-xl font-extrabold">Results</div>
                    <kiosk-org-units-table>
                        <kiosk-org-unit-row
                            v-for="(row, index) in response?.data?.data || []"
                            v-model="(response?.data?.data || [])[index]"
                            :key="row.id"
                        ></kiosk-org-unit-row>
                    </kiosk-org-units-table>
                </div>

                <div v-else-if="isEqual(defaultFilters(), fetchParams)">
                    <div v-if="favorites.length" class="text-xl font-extrabold">Favorites</div>
                    <kiosk-org-units-table v-if="favorites.length">
                        <kiosk-org-unit-row
                            v-for="(row, index) in favorites"
                            v-model="favorites[index]"
                            :key="row.id"
                        ></kiosk-org-unit-row>
                    </kiosk-org-units-table>

                    <div class="text-xl font-extrabold">Recents</div>

                    <kiosk-org-units-table>
                        <kiosk-org-unit-row
                            v-for="(row, index) in recents"
                            v-model="recents[index]"
                            :key="row.id"
                        ></kiosk-org-unit-row>
                    </kiosk-org-units-table>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import EmptyState from '~/components/general/EmptyState.vue';
import { computed, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskOrgUnitRow from '~/components/kiosk/kiosk-org-units/list/KioskOrgUnitRow.vue';
import KioskOrgUnitsControls from '~/components/kiosk/kiosk-org-units/list/KioskOrgUnitsControls.vue';
import KioskOrgUnitsSidebar from '~/components/kiosk/kiosk-org-units/details/KioskOrgUnitsSidebar.vue';
import KioskOrgUnitsTable from '~/components/kiosk/kiosk-org-units/list/KioskOrgUnitsTable.vue';
import { useToasted } from '~/composables/useToasted';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { adminOrgUnitsClient } from '~/services';
import { isEqual } from '~/utils/helpers';

const toasted = useToasted();

const defaultFilters = () => ({
    team_id: undefined,
    query: '',
    sort_dir: 'desc',
    sort_field: 'id',
    per_page: 100,
});

const fetchParams = ref(defaultFilters());

const { loading, response, fetchData } = useVersionedRequest();

const list = computed(() => response.value?.data?.data || []);

watch(
    fetchParams,
    () => {
        filterData();
    },
    {
        deep: true,
    }
);

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    filterData();
};

const filterData = () => {
    fetchData(adminOrgUnitsClient.listOrgUnits(fetchParams.value));
};

const favorites = ref([]);
const recents = ref([]);

// on first load favorite org units and recent org units are loaded
const loadInitialData = async () => {
    loading.value = true;

    try {
        const favoritesResponse = await adminOrgUnitsClient.listOrgUnits({ is_favorite: 1, per_page: 100 });
        const recentsResponse = await adminOrgUnitsClient.listOrgUnits({
            is_favorite: 0,
            sort_dir: 'desc',
            sort_field: 'id',
            per_page: 100,
        });

        favorites.value = favoritesResponse.data.data;
        recents.value = recentsResponse.data.data;
    } catch (e) {
        console.error(e);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

loadInitialData();
</script>
